import { HEN_BREEDS } from '../data/breeds';
import type { Effect, InfraDef, Stat, UpgradeDef } from '../data/types';
import { formatMult } from './format';

/**
 * Player-facing text generated from effect data. UI never hand-writes what
 * an upgrade does — it calls these, so descriptions can't drift (digest §12).
 */

const PHRASES: Record<Stat, (v: number, op: Effect['op']) => string> = {
  lay: (v) => `${formatMult(v)} laying speed`,
  value: (v) => `${formatMult(v)} egg value`,
  valueFlat: (v) => `+$${v.toFixed(2)} per egg`,
  clickValue: (v) => `${formatMult(v)} value on clicked eggs`,
  autoValue: (v) => `${formatMult(v)} value on gathered eggs`,
  gatherRate: (v) => `${formatMult(v)} gathering speed`,
  crowCooldown: (v) => `${formatMult(v)} crow cooldown`,
  crowDuration: (v) => `+${v}s crow duration`,
  crowPower: (v) => `${formatMult(v)} crow strength`,
  autoCrow: () => 'Roosters crow on their own',
  goldenSpawn: (v) => `${formatMult(v)} golden egg spawns`,
  silverSpawn: (v) => `${formatMult(v)} silver egg spawns`,
  goldenWindow: (v) => `Golden eggs stay ${v}s longer`,
  silverWindow: (v) => `Silver eggs stay ${v}s longer`,
  silverPayout: (v) => `${formatMult(v)} silver egg payouts`,
  clutchPower: (v) => (v === 2 ? 'Lucky Clutch & Prismatic bonuses doubled' : `${formatMult(v)} Lucky Clutch bonuses`),
  eventDuration: (v) => `${formatMult(v)} time to click events`,
  eventEffect: (v) => `${formatMult(v)} bonus effect duration`,
  critterSpawn: (v) => `${formatMult(v)} critter spawns`,
  hatchReward: (v) => `${formatMult(v)} brood egg rewards`,
  broodSuccess: (v) => `+${Math.round(v * 100)} pts brood success`,
  broodDuration: (v) => `${formatMult(v)} brood duration`,
  salvageWindow: (v) => `+${v}s to salvage rotten eggs`,
  offlineCapHours: (v) => `+${v}h offline earning cap`,
};

export function describeEffect(e: Effect, scopeHint?: 'flock'): string {
  let text = PHRASES[e.stat](e.value, e.op);
  if (e.scope?.kind === 'breed') text += ` for ${HEN_BREEDS[e.scope.breed].name} hens`;
  else if (e.scope?.kind === 'tier') text += ` for ${e.scope.tier} hens`;
  else if (scopeHint === 'flock') text += ' (this flock)';
  return text;
}

export function describeUpgrade(def: UpgradeDef): string {
  const parts: string[] = [];
  if (def.clickGather !== undefined) {
    parts.push(Number.isFinite(def.clickGather) ? `Collect ${def.clickGather} eggs per click` : 'Clear a whole nest in one click');
  }
  for (const e of def.effects) parts.push(describeEffect(e, def.category === 'coop' ? 'flock' : undefined));
  return parts.join(' · ');
}

export function describeInfra(def: InfraDef): string {
  return def.perUnit.map((e) => describeEffect(e)).join(' · ') + ' each';
}
